/**
 * InstagramEmbed - Alpine.js component
 *
 * Consent-safe, click-to-load facade for Instagram posts and reels.
 *
 * Instagram's official "copy embed code" hands out ~7 KB of skeleton markup per post
 * plus embed.js, which immediately builds an iframe per post and pulls in Instagram's
 * full app bundle - measured at ~2.5 MB for three reels, i.e. more than 80% of a page.
 * All of that happens before the visitor has expressed any interest, and before any
 * cookie banner has been answered.
 *
 * This component renders a local placeholder card instead and contacts Instagram only
 * after an explicit click. On click it injects the very iframe embed.js would have
 * created (https://www.instagram.com/<type>/<code>/embed/captioned/), so the script
 * itself is never needed.
 *
 * Consent: when data-ig-consent lists one or more CookieFirst categories, the card
 * shows the consent notice instead of the play button, and the button grants those
 * categories through the global $store.consent before loading. With no categories
 * configured the card is a plain click-to-load facade - still no third-party request
 * until the visitor asks for one.
 *
 * Configuration is read from data-* attributes so no localized text lives in JS.
 */

const INITIAL_HEIGHT = 640;
const ORIGIN_PATTERN = /^https:\/\/(www\.)?instagram\.com$/;

/**
 * Instagram embeds are marketing content, so consent is the default gate rather than
 * an opt-in: an element whose "required cookies" field was left empty still asks, and
 * still auto-loads once the visitor has accepted. Without this an unconfigured element
 * silently ignored CookieFirst and kept demanding a click on every page view.
 */
const DEFAULT_CONSENT = ['marketing'];

/**
 * Pull media type and shortcode out of any Instagram permalink.
 * The app copies /reels/<code> while the embed endpoint only answers to /reel/<code>.
 */
function parsePermalink(url) {
    if (!url) {
        return null;
    }

    const match = String(url).match(/instagram\.com\/(reels|reel|p|tv)\/([A-Za-z0-9_-]+)/i);
    if (!match) {
        console.warn('[InstagramEmbed] not a recognisable Instagram permalink:', url);
        return null;
    }

    return {
        type: match[1].toLowerCase() === 'reels' ? 'reel' : match[1].toLowerCase(),
        code: match[2],
    };
}

document.addEventListener('alpine:init', () => {
    Alpine.data('instagramEmbed', () => ({
        media: null,
        loaded: false,
        height: INITIAL_HEIGHT,
        consents: [],
        clickToLoad: false,
        inView: false,
        _observer: null,

        init() {
            const el = this.$root;

            this.media = parsePermalink(el.dataset.igUrl);

            const configured = (el.dataset.igConsent || '')
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean);
            this.consents = configured.length ? configured : DEFAULT_CONSENT;

            // Editors can opt a single element out of auto-loading and keep the
            // pure click-to-load card.
            this.clickToLoad = el.dataset.igClickToLoad === '1';

            if (this.clickToLoad) {
                return;
            }

            // Once consent is granted there is nothing left to ask, so the embed loads
            // on its own - but only when it comes near the viewport, so a page still
            // never pulls Instagram's ~800 KB per post that nobody scrolls to.
            this.observe();
            Alpine.effect(() => {
                // Reading the list here is what subscribes this effect to consent changes.
                this.$store.consent.list.length;
                if (this.loaded || !this.inView || this.needsConsent) {
                    return;
                }
                this.load();
            });
        },

        /** Auto-loading stays lazy: only cards near the viewport are fetched. */
        observe() {
            if (!('IntersectionObserver' in window)) {
                this.inView = true;
                return;
            }

            this._observer = new IntersectionObserver((entries, obs) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.inView = true;
                        obs.unobserve(entry.target);
                    }
                });
            }, { rootMargin: '200px 0px' });

            this._observer.observe(this.$root);
        },

        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
        },

        /** Permalink to the post itself - used for the "open on Instagram" fallback link. */
        get permalink() {
            return this.media
                ? `https://www.instagram.com/${this.media.type}/${this.media.code}/`
                : '';
        },

        /** The iframe URL embed.js would have built. */
        get embedSrc() {
            return this.media
                ? `https://www.instagram.com/${this.media.type}/${this.media.code}/embed/captioned/`
                : '';
        },

        /** True while the configured consent categories have not been granted yet. */
        get needsConsent() {
            if (!this.consents.length) {
                return false;
            }

            const store = this.$store.consent;
            // Do not flash the consent card before CookieFirst has answered.
            if (store && !store.ready) {
                return true;
            }

            if (!store) {
                console.warn('[InstagramEmbed] consent categories configured but no $store.consent found');
                return true;
            }

            return !store.has(this.consents);
        },

        /**
         * Single visitor action: grant the configured categories (if any) and load.
         * Nothing leaves the browser before this runs.
         */
        accept() {
            if (this.consents.length && this.$store.consent) {
                this.$store.consent.allow(this.consents);
            }

            this.load();
        },

        load() {
            if (!this.media) {
                console.warn('[InstagramEmbed] nothing to load, permalink could not be parsed');
                return;
            }

            this.loaded = true;
        },

        /**
         * The embed iframe posts its rendered height to the parent window; that is what
         * embed.js listens for to size the frame. Same contract, without the script.
         */
        onMessage(event) {
            if (!this.loaded || !ORIGIN_PATTERN.test(event.origin)) {
                return;
            }

            // The iframe lives inside an x-if template, so resolve it from the
            // component root rather than through $refs.
            const frame = this.$root.querySelector('iframe.ig-embed__frame');
            if (!frame || frame.contentWindow !== event.source) {
                return;
            }

            let payload = event.data;
            if (typeof payload === 'string') {
                try {
                    payload = JSON.parse(payload);
                } catch (error) {
                    return;
                }
            }

            if (!payload || payload.type !== 'MEASURE' || !payload.details) {
                return;
            }

            const height = parseInt(payload.details.height, 10);
            if (!height || height === this.height) {
                return;
            }

            this.height = height;
        },
    }));
});
